import { MovingAverage } from './moving_average';
/**
 * Default initial bandwidth.
 *
 * This is used if the runtime doesn't provide acces to the
 * navigator.connection API. The value is 500kBps, which is 2g speed.
 */
const UNKNOWN_BANDWIDTH_DEFAULT = 4e5;
/**
 * Minimum size of a sample for it to be used in the calculation of the average.
 */
const MIN_SAMPLE_THRESHOLD = 16e3; // 16kB;
/**
 * Minimum number of bytes to have sampled before accepting the average from
 * the exponential moving averages (EMAs).
 */
const MIN_TOTAL_THRESHOLD = 128e3; // 128kB;
/**
 * Creates a new BandwidthEstimator.
 */
export class BandwidthEstimator {
    constructor(defaultBandwidth = UNKNOWN_BANDWIDTH_DEFAULT) {
        /**
         * Total amount of bytes used for sampling the bandwidth.
         */
        this._totalBytes = 0;
        /**
         * When set to a truthy value, this will override the recommended bandwidth
         * estimate returned from getBandwidthEstimate.
         */
        this._overrideBitrate = 0;
        this._currentBandwidth = defaultBandwidth;
        this._short = new MovingAverage(2);
        this._long = new MovingAverage(5);
        this._init();
    }
    /**
     * Initialize the instance.
     */
    _init() {
        if (typeof navigator === 'undefined') {
            return;
        }
        const nav = navigator;
        if (nav.connection && nav.connection.downlink) {
            this._currentBandwidth = nav.connection.downlink * 1e6;
            this._onConnectionChange = this._onConnectionChange.bind(this);
            nav.connection.addEventListener('change', this._onConnectionChange);
        }
    }
    /**
     * Handles change events fired from `navigator.connection`.
     *
     * @param e - The ConnectionChange event from the NetworkInformation object.
     */
    _onConnectionChange(e) {
        const target = e.target;
        const bandwidth = target.downlink || 0;
        this._setBandwidth(bandwidth);
    }
    /**
     * Set current bandwidth.
     *
     * @param bandwidth - The value of the current bandwidth.
     */
    _setBandwidth(bandwidth) {
        this._currentBandwidth = bandwidth * 1e6;
    }
    /**
     * Returns the current bandwidth estimate, calculated from the minum value of
     * the short and long windowed averages respectively.
     *
     * @return The current estimated bandwidth.
     */
    getBandwidthEstimate() {
        if (this._overrideBitrate) {
            return this._overrideBitrate;
        }
        if (this._totalBytes < MIN_TOTAL_THRESHOLD) {
            return this._currentBandwidth;
        }
        const fast = this._short.getEstimate();
        const slow = this._long.getEstimate();
        return Math.min(fast, slow);
    }
    /**
     * Add a sample to the estimator.
     *
     * @param bytes - The number of bytes in the sample.
     * @param time - The amount of time it took the fetch the bytes.
     */
    sample(bytes, time) {
        if (bytes < MIN_SAMPLE_THRESHOLD) {
            return;
        }
        const bandwidth = (bytes * 8000) / time;
        this._totalBytes += bytes;
        this._short.sample(time, bandwidth);
        this._long.sample(time, bandwidth);
    }
    /**
     * Overrides bitrate recommendation value with a specific value.
     *
     * @param value - The new overriding value.
     */
    overrideBitrate(value) {
        if (value <= 0) {
            this._overrideBitrate = 0;
        }
        else {
            this._overrideBitrate = value;
        }
    }
}
//# sourceMappingURL=bandwidth_estimator.js.map